import { FC, useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useTranslation } from 'react-i18next';
import Form from 'components/form/Form';
import Button from 'components/Button';
import FormButtons from 'components/form/FormButtons';
import TooltipWrapper from 'components/TooltipWrapper';
import FormGroup from 'components/form/FormGroup';

interface IProps {
  methods: any;
  data: any;
  onSubmit: (data: any) => void;
  onDownloadXML: (data: any) => void;
  onDownloadAladdin: (data: any) => void;
  onDownloadBusinessNavigator: (data: any) => void;
  onDownloadStil: (data: any) => void;
  reportParams?: any;
  isLoadingXML: boolean;
  isLoadingAladdin: boolean;
  isLoadingBusinessNavigator: boolean;
  isLoadingStil?: boolean;
}
interface IDataType {
  [key: string]: string | number | boolean;
}

const ReportForm: FC<IProps> = ({
  methods,
  data,
  onSubmit,
  onDownloadXML,
  onDownloadAladdin,
  onDownloadBusinessNavigator,
  onDownloadStil,
  isLoadingXML,
  isLoadingAladdin,
  isLoadingBusinessNavigator,
  isLoadingStil,
}) => {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState(data?.user || []);
  const { formState } = methods;
  const { isSubmitting } = formState;

  useEffect(() => {
    // Set default values for the filters
    methods.setValue('date_from', new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    methods.setValue('date_to', new Date());
    methods.setValue(
      'type',
      data?.type.filter((j: IDataType) => j.selected)
    );
    methods.setValue(
      'status',
      data?.status.filter((j: IDataType) => j.selected)
    );
  }, []);

  const groups = methods.watch('group_id');

  useEffect(() => {
    const groupIds = groups?.map((gr: any) => gr.value);

    /**
     * When a group is selected, filter employee options
     * to only employees from groups selected and
     * automatically select all employees
     *
     * If no group selected - clear selected employees
     */
    if (groupIds) {
      const filteredEmployees = data?.user.filter((empl: any) => {
        /**
         * If employee doesn't have any groups check if "No group"
         * is selected which value is set to 0 from BE
         */
        if (!empl.groups.length) return groupIds.includes('null');

        const isInSelectedGroups = empl.groups.reduce((flag: boolean, currGr: number) => {
          return flag || groupIds.includes(currGr);
        }, false);
        return isInSelectedGroups;
      });

      setEmployees(filteredEmployees);
      methods.setValue('user_id', filteredEmployees);
    } else {
      setEmployees(data?.user);
      methods.setValue('user_id', null);
    }
  }, [groups]);

  return (
    <Form
      methods={methods}
      btnLabel={t('reports.downloadLeavesReport')}
      hideAllBtns
      onSubmit={onSubmit}
    >
      <Row>
        <Col
          lg={{ span: 6 }}
          xl={{
            offset: 2,
            span: 4,
          }}
        >
          <FormGroup
            controlId="formGroupGroups"
            label={t('reports.groups')}
            type="select"
            as="select"
            name="group_id"
            isMulti
            isClearable
            isSearchable
            allowSelectAll
            selectOptions={data?.group}
          />
        </Col>
        <Col
          lg={{ span: 6 }}
          xl={4}
        >
          <FormGroup
            controlId="formGroupEmployees"
            label={t('reports.employees')}
            type="select"
            as="select"
            name="user_id"
            isMulti
            isClearable
            isSearchable
            allowSelectAll
            showTotal
            selectOptions={employees}
            limitOptions
          />
        </Col>
      </Row>
      <Row>
        <Col
          xl={{
            offset: 2,
            span: 4,
          }}
        >
          <Row>
            <Col>
              <FormGroup
                controlId="formGroupFromDate"
                label={t('reports.fromDate')}
                type="datepicker"
                name="date_from"
                dateFormat="dd.MM.yyyy"
                showMonthDropdown
                showYearDropdown
                isRequired
              />
            </Col>
            <Col>
              <FormGroup
                controlId="formGroupToDate"
                label={t('reports.toDate')}
                type="datepicker"
                name="date_to"
                dateFormat="dd.MM.yyyy"
                showMonthDropdown
                showYearDropdown
                isRequired
              />
            </Col>
          </Row>
        </Col>
        <Col
          lg={{ span: 6 }}
          xl={2}
        >
          <FormGroup
            controlId="formGroupLeaveType"
            label={t('reports.leaveType')}
            type="select"
            as="select"
            name="type"
            isMulti
            isClearable
            isRequired
            isSearchable
            allowSelectAll
            selectOptions={data?.type}
          />
        </Col>
        <Col
          lg={{ span: 6 }}
          xl={2}
        >
          <FormGroup
            controlId="formGroupLeaveStatus"
            label={t('reports.leaveStatus')}
            type="select"
            as="select"
            name="status"
            isMulti
            isClearable
            isRequired
            isSearchable
            allowSelectAll
            selectOptions={data?.status}
          />
        </Col>
      </Row>
      <div
        className="d-flex justify-content-center flex-wrap"
        style={{ gap: '32px', alignItems: 'flex-end' }}
      >
        <FormButtons
          btnLabel={t('reports.downloadLeavesReport')}
          isSubmitting={isSubmitting}
          hideCancelBtn
        />
        <Button
          type="button"
          btnType="secondary"
          disabled={isLoadingXML}
          loading={isLoadingXML}
          onClick={() => onDownloadXML(methods.getValues())}
          style={{ height: '35px' }}
        >
          {t('reports.downloadXML')}
        </Button>
        <Button
          type="button"
          btnType="secondary"
          disabled={isLoadingAladdin}
          loading={isLoadingAladdin}
          onClick={() => onDownloadAladdin(methods.getValues())}
          style={{ height: '35px' }}
        >
          {t('reports.downloadAladdin')}
        </Button>
        <TooltipWrapper
          id="tooltip-print-download-business-navigator"
          overlay={t('reports.noLeavesAndSick')}
        >
          <Button
            type="button"
            btnType="secondary"
            disabled={isLoadingBusinessNavigator}
            loading={isLoadingBusinessNavigator}
            onClick={() => onDownloadBusinessNavigator(methods.getValues())}
            style={{ height: '35px' }}
          >
            {t('reports.downloadBusinessNavigator')}
          </Button>
        </TooltipWrapper>
        <TooltipWrapper
          id="tooltip-print-download-stil"
          overlay={t('reports.noLeavesAndSick')}
        >
          <Button
            type="button"
            btnType="secondary"
            disabled={isLoadingStil}
            loading={isLoadingStil}
            onClick={() => onDownloadStil(methods.getValues())}
            style={{ height: '35px' }}
          >
            {t('reports.downloadStil')}
          </Button>
        </TooltipWrapper>
      </div>
    </Form>
  );
};

export default ReportForm;
